import { RefObject, useEffect, useRef } from 'react';
import { FiTrash2 } from 'react-icons/fi';

import Button from 'components/ui/button';
import { ModalFrame } from 'components/ui/modal-frame';
import { useHelp } from 'context/HelpContext';
import { useModal } from 'context/ModalContext';

import { BehaviorStep } from './components/behavior-step';
import { CoinPointsStep } from './components/coin-points-step';
import { ColleagueStep } from './components/colleague-step';
import { MessageStep } from './components/message-step';
import { RecognitionAlert, RelationshipConfirm } from './components/recognition-alert';
import { RecognitionFailed, RecognitionSent, SendingState } from './components/recognition-result';
import { RecognitionStepper } from './components/recognition-stepper';
import { ReviewStep } from './components/review-step';
import { colleagueShortName } from './components/colleague';
import { ActionBar, STEP_HEADING_ATTRIBUTE } from './components/step-layout';
import { STEP, primaryActionLabel } from './recognition-steps';
import { useRecognitionFlow } from './use-recognition-flow';

const HELP_VIDEO_MODAL = 'help-video';

type RecognitionFlow = ReturnType<typeof useRecognitionFlow>;

/**
 * Quando a etapa (ou a tela de resultado) troca, a tela nova abre do começo: a rolagem do corpo
 * volta ao topo e o foco vai para o título. Quem navega por teclado ou leitor de tela cai no
 * começo do conteúdo novo, e não no botão que sumiu (REC-04).
 *
 * A rolagem é levada ao topo aqui, e não deixada por conta do foco: o corpo é o mesmo elemento
 * entre as etapas, e o título da revisão é só para o leitor de tela, fora do fluxo, então o foco
 * nele não fazia o navegador rolar e a revisão abria no fim.
 *
 * Não roda na abertura: ali o foco é do próprio diálogo.
 */
function useStartOfScreenOnChange(scrollAreaRef: RefObject<HTMLElement>, currentScreen: string) {
  const shownScreen = useRef(currentScreen);

  useEffect(() => {
    if (shownScreen.current === currentScreen) return;

    shownScreen.current = currentScreen;

    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    scrollArea.scrollTop = 0;
    scrollArea.querySelector<HTMLElement>(`[${STEP_HEADING_ATTRIBUTE}]`)?.focus({ preventScroll: true });
  }, [scrollAreaRef, currentScreen]);
}

function CurrentStep({ flow }: { flow: RecognitionFlow }) {
  const { step, target, behavior, coin, points, recognition } = flow;
  const colleagueName = colleagueShortName(target);

  if (step === STEP.colleague || !target) {
    return (
      <ColleagueStep
        target={target}
        search={flow.search}
        initialTerm={flow.initialSearchTerm}
        onSearch={flow.searchColleague}
        onTyping={flow.cancelAutoPick}
        onSelect={flow.selectTarget}
        onRemove={flow.removeTarget}
      />
    );
  }

  if (step === STEP.behavior) {
    return (
      <BehaviorStep
        colleagueName={colleagueName}
        colleagueIsLeader={!!target.isLeader}
        behavior={behavior}
        onSelect={flow.selectBehavior}
      />
    );
  }

  if (step === STEP.coinPoints) {
    return (
      <CoinPointsStep
        colleagueName={colleagueName}
        behavior={behavior}
        coin={coin}
        points={points}
        balance={flow.balance}
        pointsSent={flow.pointsSent}
        onSelectCoin={flow.selectCoin}
        onSelectPoints={flow.selectPoints}
      />
    );
  }

  if (!behavior || !coin) return null;

  if (step === STEP.message) {
    return (
      <MessageStep
        colleagueName={colleagueName}
        coin={coin}
        behavior={behavior}
        option={flow.messageOption}
        freeText={flow.freeText}
        onSelectOption={flow.selectMessageOption}
        onChangeFreeText={flow.changeFreeText}
      />
    );
  }

  return recognition ? <ReviewStep recognition={recognition} /> : null;
}

function StepActions({ flow }: { flow: RecognitionFlow }) {
  return (
    <ActionBar className="mt-2.5">
      {flow.canClearDraft && (
        <Button variant="texto" onClick={flow.clearDraft} aria-label="Limpar rascunho salvo e começar do início">
          <FiTrash2 aria-hidden="true" size={15} className="mr-2" />
          Limpar rascunho
        </Button>
      )}

      {flow.step > STEP.colleague && (
        <Button variant="texto" onClick={flow.goBack}>
          Voltar
        </Button>
      )}

      <Button variant="solido-destaque" disabled={!flow.canAdvance} onClick={flow.goNext}>
        {primaryActionLabel(flow.step)}
      </Button>
    </ActionBar>
  );
}

/**
 * Modal "Reconheça quem #JogaJunto!" (`/reconhecer` e `/reconhecer/:id`): colega,
 * comportamento, tipo e pontos, mensagem, revisão e o resultado do envio.
 */
export default function Reconhecer() {
  const flow = useRecognitionFlow();
  const { showModal, openModal } = useModal();
  const { helpData } = useHelp();
  const bodyRef = useRef<HTMLDivElement>(null);

  useStartOfScreenOnChange(bodyRef, `${flow.view}:${flow.step}:${flow.target ? 'com-colega' : 'sem-colega'}`);

  if (flow.hidden) return null;

  const { view, result } = flow;
  const showingSteps = view === 'steps';

  return (
    <>
      {/* O vídeo de ajuda é um modal da pele antiga, que não empilha sobre este: enquanto ele
          está aberto, este some e volta com as escolhas no lugar (REC-58). */}
      <ModalFrame
        isOpen={openModal !== HELP_VIDEO_MODAL}
        title="Reconheça quem #JogaJunto!"
        onClose={flow.close}
        closeLabel="Fechar reconhecimento"
        headerExtra={showingSteps ? <RecognitionStepper step={flow.step} /> : undefined}
      >
        {/* O corpo rola aqui, e não na moldura, para poder reservar o pé da área de rolagem
            (`scroll-pb`) à faixa de ações: sem a reserva, o Tab levava o foco a um campo que ficava
            inteiro atrás dela (WCAG 2.4.11). A reserva cobre a faixa em duas linhas, como fica no
            celular com "Limpar rascunho". O recuo fica na div de dentro: na que rola, o recuo de
            baixo afastava a faixa presa do pé do cartão e o conteúdo aparecia por baixo dela. */}
        <div ref={bodyRef} className="min-h-0 flex-1 scroll-pb-32 overflow-y-auto">
          <div className="px-4 pb-3 pt-5 sm:px-8 sm:pb-3.5 sm:pt-6">
            {view === 'sending' && <SendingState />}

            {view === 'sent' && result && (
              <RecognitionSent serverMessage={result.serverMessage} recognition={result.recognition} onClose={flow.close} />
            )}

            {view === 'failed' && result && (
              <RecognitionFailed serverMessage={result.serverMessage} onRetry={flow.canRetry ? flow.retry : undefined} />
            )}

            {showingSteps && (
              <>
                <CurrentStep flow={flow} />
                <StepActions flow={flow} />

                {!!helpData && (
                  <p className="m-0 mt-1 pb-3 text-center text-xs text-content">
                    Dúvidas para enviar um reconhecimento?{' '}
                    <button
                      type="button"
                      onClick={() => showModal(HELP_VIDEO_MODAL)}
                      className="font-bold text-brand-blue-dark underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark"
                    >
                      Assistir vídeo
                    </button>
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </ModalFrame>

      {flow.alert && <RecognitionAlert alert={flow.alert} onClose={flow.dismissAlert} />}

      {flow.askingRelationship && (
        <RelationshipConfirm onConfirm={flow.confirmRelationship} onDismiss={flow.dismissRelationship} />
      )}
    </>
  );
}
